import * as React from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Stack,
  Text,
  useColorModeValue
} from '@chakra-ui/react';
import { FiBell } from 'react-icons/fi';
import LocalizedLink from './localizedLink';
import { NewsletterTurnstile, NewsletterTurnstileHandle } from './newsletterTurnstile';
import { useTranslationsContext } from './useTranslations';
import { StaticAssetImage } from './staticAssetImage';

export type NewsletterSource = 'footer' | 'car-detail' | 'popup' | 'newsletter-page' | 'team-microsite';
export type NewsletterVariant = 'footer' | 'inline' | 'detail' | 'modal' | 'landing';

type NewsletterContextValue = {
  enabled: boolean;
  loading: boolean;
  locale: string;
  turnstileSiteKey: string | null;
  subscribe: (email: string, source: NewsletterSource, turnstileToken: string | null) => Promise<void>;
};

const NewsletterContext = React.createContext<NewsletterContextValue | undefined>(undefined);
const STORAGE_KEY = 'acm_newsletter_prompt';
const SUBMITTED_EVENT = 'acm-newsletter-submitted';
const OPEN_EVENT = 'acm-newsletter-open';
const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
const ONE_YEAR = 365 * 24 * 60 * 60 * 1000;
const NEWSLETTER_MOCK_ENABLED = process.env.NODE_ENV === 'development' && process.env.GATSBY_NEWSLETTER_MOCK === 'true';
const NEWSLETTER_LOCAL_API_ENABLED =
  process.env.NODE_ENV === 'development' && process.env.GATSBY_NEWSLETTER_LOCAL_API === 'true';
const NEWSLETTER_MOCK_ERROR_EMAIL = 'error@newsletter.test';
const NEWSLETTER_MOCK_DELAY_MS = 650;
const NEWSLETTER_REQUEST_TIMEOUT_MS = 25000;
const NEWSLETTER_POPUP_DELAY_MS = 30000;
const NEWSLETTER_POPUP_EXIT_INTENT_DELAY_MS = 20000;
const NEWSLETTER_POPUP_RETRY_MS = 1000;
type NewsletterStatus = { enabled: boolean; turnstileSiteKey: string | null };
const newsletterStatusRequests = new Map<string, Promise<NewsletterStatus>>();

type StoredPreference = {
  state: 'dismissed' | 'pending' | 'confirmed';
  expiresAt: number;
};

function languageFromLocale(locale: string): 'de' | 'en' | 'fr' {
  const language = locale.split('-')[0];
  return language === 'en' || language === 'fr' ? language : 'de';
}

function readPreference(): StoredPreference | null {
  if (typeof window === 'undefined') return null;
  try {
    const value = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null') as StoredPreference | null;
    if (!value || value.expiresAt <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return value;
  } catch {
    return null;
  }
}

function storePreference(state: StoredPreference['state'], duration: number): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ state, expiresAt: Date.now() + duration }));
  } catch {
    // Storage can be unavailable in private browsing. The form still works without it.
  }
}

function getNewsletterStatus(language: string): Promise<NewsletterStatus> {
  const cachedRequest = newsletterStatusRequests.get(language);
  if (cachedRequest) return cachedRequest;

  const request = NEWSLETTER_MOCK_ENABLED
    ? Promise.resolve({ enabled: true, turnstileSiteKey: null })
    : fetch(`/api/newsletter?locale=${language}`, { headers: { Accept: 'application/json' } })
        .then(async response => {
          if (!response.ok) throw new Error('Newsletter status is unavailable');
          const body = (await response.json()) as { enabled?: boolean; turnstileSiteKey?: unknown };
          const turnstileSiteKey =
            typeof body.turnstileSiteKey === 'string' && body.turnstileSiteKey.trim()
              ? body.turnstileSiteKey.trim()
              : null;
          return {
            enabled: Boolean(body.enabled && (turnstileSiteKey || NEWSLETTER_LOCAL_API_ENABLED)),
            turnstileSiteKey
          };
        })
        .catch(() => {
          newsletterStatusRequests.delete(language);
          return { enabled: false, turnstileSiteKey: null };
        });

  newsletterStatusRequests.set(language, request);
  return request;
}

export function markNewsletterConfirmed(): void {
  storePreference('confirmed', ONE_YEAR);
}

export const NewsletterProvider: React.FC<{ locale: string; children: React.ReactNode }> = ({ locale, children }) => {
  const [enabled, setEnabled] = React.useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const language = languageFromLocale(locale);

  React.useEffect(() => {
    let isCurrent = true;
    setLoading(true);
    setEnabled(false);
    setTurnstileSiteKey(null);

    getNewsletterStatus(language).then(status => {
      if (!isCurrent) return;
      setEnabled(status.enabled);
      setTurnstileSiteKey(status.turnstileSiteKey);
      setLoading(false);
    });

    return () => {
      isCurrent = false;
    };
  }, [language]);

  const subscribe = React.useCallback(
    async (email: string, source: NewsletterSource, turnstileToken: string | null) => {
      if (NEWSLETTER_MOCK_ENABLED) {
        await new Promise<void>(resolve => window.setTimeout(resolve, NEWSLETTER_MOCK_DELAY_MS));
        if (email.toLowerCase() === NEWSLETTER_MOCK_ERROR_EMAIL) {
          throw new Error('NEWSLETTER_MOCK_ERROR');
        }
      } else {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), NEWSLETTER_REQUEST_TIMEOUT_MS);

        try {
          const response = await fetch('/api/newsletter', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ email, locale: language, source, consent: true, turnstileToken }),
            signal: controller.signal
          });

          if (!response.ok) {
            const body = (await response.json().catch(() => null)) as { code?: string } | null;
            const error = new Error(body?.code ?? 'NEWSLETTER_SUBMISSION_FAILED');
            error.name = body?.code ?? 'NEWSLETTER_SUBMISSION_FAILED';
            throw error;
          }
        } finally {
          window.clearTimeout(timeout);
        }
      }

      storePreference('pending', THIRTY_DAYS);
      window.dispatchEvent(new CustomEvent<NewsletterSource>(SUBMITTED_EVENT, { detail: source }));
    },
    [language]
  );

  const value = React.useMemo(
    () => ({ enabled, loading, locale, turnstileSiteKey, subscribe }),
    [enabled, loading, locale, subscribe, turnstileSiteKey]
  );

  return <NewsletterContext.Provider value={value}>{children}</NewsletterContext.Provider>;
};

function useNewsletter(): NewsletterContextValue {
  const context = React.useContext(NewsletterContext);
  if (!context) throw new Error('Newsletter components must be rendered inside NewsletterProvider.');
  return context;
}

export const NewsletterPopupTrigger: React.FC<{ label: string }> = ({ label }) => {
  const { enabled, loading } = useNewsletter();

  if (loading || !enabled) return null;

  return (
    <Button
      type="button"
      variant="link"
      color="red.200"
      leftIcon={<FiBell />}
      fontSize="sm"
      fontWeight="semibold"
      minH="44px"
      px={0}
      aria-haspopup="dialog"
      onClick={event => window.dispatchEvent(new CustomEvent<HTMLElement>(OPEN_EVENT, { detail: event.currentTarget }))}
      _hover={{ color: 'white', textDecoration: 'underline', textUnderlineOffset: '3px' }}
      _focusVisible={{ boxShadow: 'outline', borderRadius: 'sm' }}
    >
      {label}
    </Button>
  );
};

type SignupProps = {
  source: NewsletterSource;
  variant: NewsletterVariant;
  heading?: string;
};

export const NewsletterSignup: React.FC<SignupProps> = ({ source, variant, heading }) => {
  const {
    newsletter_eyebrow,
    newsletter_title,
    newsletter_detail_title,
    newsletter_landing_form_title,
    newsletter_description,
    newsletter_email_label,
    newsletter_email_placeholder,
    newsletter_consent_before,
    newsletter_consent_link,
    newsletter_consent_after,
    newsletter_submit,
    newsletter_success,
    newsletter_error_invalid_email,
    newsletter_error_consent,
    newsletter_error_verification,
    newsletter_error_generic,
    newsletter_unavailable
  } = useTranslationsContext();
  const { enabled, loading, locale, subscribe, turnstileSiteKey } = useNewsletter();
  const [email, setEmail] = React.useState('');
  const [consent, setConsent] = React.useState(false);
  const [status, setStatus] = React.useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [errorKind, setErrorKind] = React.useState<'email' | 'consent' | 'submission' | null>(null);
  const turnstileRef = React.useRef<NewsletterTurnstileHandle>(null);
  const errorId = React.useId();
  const privacyPath = '/privacy-policy';
  const isFooter = variant === 'footer';
  const isLanding = variant === 'landing';
  const isDetail = variant === 'detail';
  const title = heading ?? (variant === 'detail' ? newsletter_detail_title : isLanding ? newsletter_landing_form_title : newsletter_title);
  const footerColor = useColorModeValue('gray.900', 'white');
  const footerTextColor = useColorModeValue('gray.600', 'gray.300');
  const footerAccentColor = useColorModeValue('red.700', 'red.200');
  const surfaceColor = isFooter ? footerColor : 'white';
  const supportingColor = isFooter ? footerTextColor : 'gray.200';
  const accentColor = isFooter ? footerAccentColor : 'red.200';
  const isSubmitting = status === 'submitting';

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    setErrorMessage('');
    setErrorKind(null);

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setStatus('error');
      setErrorKind('email');
      setErrorMessage(newsletter_error_invalid_email);
      return;
    }
    if (!consent) {
      setStatus('error');
      setErrorKind('consent');
      setErrorMessage(newsletter_error_consent);
      return;
    }

    setStatus('submitting');
    try {
      const requiresTurnstile = !NEWSLETTER_MOCK_ENABLED && !NEWSLETTER_LOCAL_API_ENABLED;
      const turnstileToken = requiresTurnstile ? await turnstileRef.current?.verify() : null;
      if (requiresTurnstile && !turnstileToken) throw new Error('CHALLENGE_UNAVAILABLE');
      await subscribe(email.trim(), source, turnstileToken ?? null);
      setStatus('success');
    } catch (error) {
      turnstileRef.current?.reset();
      setStatus('error');
      setErrorKind('submission');
      const isVerificationError =
        error instanceof Error &&
        [error.name, error.message].some(
          value => value.startsWith('TURNSTILE_') || value.startsWith('CHALLENGE_') || value.includes('VERIFICATION')
        );
      setErrorMessage(isVerificationError ? newsletter_error_verification : newsletter_error_generic);
    }
  };

  return (
    <Box
      data-newsletter-source={source}
      bg={variant === 'modal' || isFooter || isLanding ? 'transparent' : isDetail ? 'whiteAlpha.100' : 'gray.800'}
      color={surfaceColor}
      borderWidth={isDetail ? '1px' : 0}
      borderLeftWidth={isFooter || variant === 'modal' || isLanding || isDetail ? 0 : '4px'}
      borderLeftStyle="solid"
      borderColor={isDetail ? 'whiteAlpha.200' : 'brand'}
      borderRadius={variant === 'modal' || isFooter || isLanding ? 0 : 'lg'}
      px={isFooter || isLanding ? 0 : [5, null, 6]}
      py={isFooter || isLanding ? 0 : [5, null, 6]}
      w="full"
    >
      <Flex
        direction={isLanding || isDetail ? 'column' : ['column', null, 'row']}
        gap={isDetail ? 4 : [4, null, 6]}
        align="stretch"
      >
        <Box flex="1" minW={0}>
          {!isLanding && (
            <Flex align="center" gap={2} color={accentColor} mb={isFooter ? 1 : 2}>
              <Icon as={FiBell} boxSize={4} aria-hidden />
              <Text fontSize="xs" fontWeight="bold" letterSpacing="0.12em" textTransform="uppercase">
                {newsletter_eyebrow}
              </Text>
            </Flex>
          )}
          <Heading as={variant === 'modal' || isLanding ? 'h2' : 'h3'} size={isFooter || isDetail ? 'sm' : 'md'} mb={1}>
            {title}
          </Heading>
          <Text color={supportingColor} fontSize="sm" maxW="2xl">
            {newsletter_description}
          </Text>
        </Box>

        <Box as="form" onSubmit={handleSubmit} flex="1.25" minW={0} noValidate>
          {status === 'success' ? (
            <Alert
              status="success"
              role="status"
              aria-live="polite"
              aria-atomic="true"
              alignItems="flex-start"
              bg="#E6FFFA"
              color="#1C4532"
              border="1px solid"
              borderColor="#2F855A"
              borderRadius="md"
              px={4}
              py={3}
            >
              <AlertIcon color="#22543D" flexShrink={0} mt="2px" aria-hidden />
              <AlertDescription fontSize="sm" fontWeight="medium" lineHeight="tall">
                {newsletter_success}
              </AlertDescription>
            </Alert>
          ) : (
            <Stack spacing={3}>
              <FormControl
                isRequired
                isDisabled={!enabled || loading || isSubmitting}
                isInvalid={errorKind === 'email'}
              >
                <FormLabel fontSize="sm" mb={1}>
                  {newsletter_email_label}
                </FormLabel>
                <Flex direction="column" gap={2} align="flex-start">
                  <Input
                    type="email"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    placeholder={newsletter_email_placeholder}
                    autoComplete="email"
                    bg="white"
                    color="black"
                    borderColor="gray.300"
                    w="full"
                    minH="44px"
                    aria-describedby={errorKind === 'email' ? errorId : undefined}
                    _placeholder={{ color: 'gray.500' }}
                    _focusVisible={{ borderColor: 'brand', boxShadow: '0 0 0 1px var(--chakra-colors-brand)' }}
                  />
                  <Button
                    type="submit"
                    variant="brandPrimary"
                    isLoading={isSubmitting}
                    isDisabled={!enabled || loading || isSubmitting}
                    flexShrink={0}
                    fontSize="sm"
                    px={3}
                    whiteSpace="nowrap"
                    alignSelf={isDetail ? 'stretch' : 'flex-start'}
                    minH="44px"
                  >
                    {newsletter_submit}
                  </Button>
                </Flex>
              </FormControl>
              <Checkbox
                colorScheme="red"
                isChecked={consent}
                onChange={event => setConsent(event.target.checked)}
                isDisabled={!enabled || loading || isSubmitting}
                isInvalid={errorKind === 'consent'}
                aria-describedby={errorKind === 'consent' ? errorId : undefined}
                alignItems="flex-start"
                size="sm"
              >
                <Text fontSize="xs" color={supportingColor} lineHeight="tall">
                  {newsletter_consent_before}{' '}
                  <LocalizedLink
                    to={privacyPath}
                    color={accentColor}
                    textDecoration="underline"
                    textUnderlineOffset="2px"
                    _focusVisible={{ outline: '3px solid', outlineColor: 'brand', outlineOffset: '2px' }}
                  >
                    {newsletter_consent_link}
                  </LocalizedLink>{' '}
                  {newsletter_consent_after}
                </Text>
              </Checkbox>
              {!NEWSLETTER_MOCK_ENABLED && !NEWSLETTER_LOCAL_API_ENABLED && turnstileSiteKey && (
                <NewsletterTurnstile
                  ref={turnstileRef}
                  language={languageFromLocale(locale)}
                  siteKey={turnstileSiteKey}
                />
              )}
              {!loading && !enabled && (
                <Text fontSize="sm" color={supportingColor} aria-live="polite">
                  {newsletter_unavailable}
                </Text>
              )}
              {status === 'error' && (
                <Alert
                  id={errorId}
                  status="error"
                  role="alert"
                  aria-atomic="true"
                  alignItems="flex-start"
                  bg="#FFF5F5"
                  color="#742A2A"
                  border="1px solid"
                  borderColor="#C53030"
                  borderRadius="md"
                  px={4}
                  py={3}
                >
                  <AlertIcon color="#9B2C2C" flexShrink={0} mt="2px" aria-hidden />
                  <AlertDescription fontSize="sm" fontWeight="medium" lineHeight="tall">
                    {errorMessage}
                  </AlertDescription>
                </Alert>
              )}
            </Stack>
          )}
        </Box>
      </Flex>
    </Box>
  );
};

function isExcludedPath(pathname: string): boolean {
  return [
    /\/(privacy-policy|imprint|accessibility)\/?$/,
    /\/newsletter\/?$/,
    /\/newsletter-confirmed\/?$/,
    /\/contact\/?$/,
    /\/cars\/[^/]+\/(request|financing-request|trade-in)\/?$/,
    /\/services\/(car-purchase-request|financing)\/?$/,
    /\/trade-in\/search\/?$/
  ].some(pattern => pattern.test(pathname));
}

export const NewsletterPopup: React.FC = () => {
  const { enabled, loading } = useNewsletter();
  const { close: closeLabel, newsletter_title } = useTranslationsContext();
  const [isOpen, setIsOpen] = React.useState(false);
  const openerRef = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    const forceMockPopup =
      (NEWSLETTER_MOCK_ENABLED || NEWSLETTER_LOCAL_API_ENABLED) &&
      new URLSearchParams(window.location.search).get('newsletter-popup') === '1';
    if (loading || !enabled) return;

    let armed = false;
    let openTimer: number | undefined;
    let armTimer: number | undefined;
    const isTouch = window.matchMedia('(pointer: coarse)').matches;
    const canShowAutomatically = forceMockPopup || (!readPreference() && !isExcludedPath(window.location.pathname));

    const canOpen = (ignorePreference = false) =>
      (ignorePreference || forceMockPopup || !readPreference()) &&
      !document.querySelector('[role="dialog"]:not([aria-hidden="true"])') &&
      !document.querySelector('#cc--main.show--consent, #cc--main .show--consent');

    const open = (ignorePreference = false) => {
      if (!canOpen(ignorePreference)) return false;
      setIsOpen(true);
      return true;
    };
    const scheduleAutomaticOpen = (delay: number) => {
      if (openTimer) window.clearTimeout(openTimer);
      openTimer = window.setTimeout(openAutomaticallyWhenAvailable, delay);
    };
    function openAutomaticallyWhenAvailable() {
      if (openTimer) window.clearTimeout(openTimer);
      openTimer = undefined;
      openerRef.current = null;
      if (open()) return;
      if (!readPreference()) scheduleAutomaticOpen(NEWSLETTER_POPUP_RETRY_MS);
    }
    const handleOpenRequested = (event: Event) => {
      const requestedBy = (event as CustomEvent<HTMLElement>).detail;
      openerRef.current =
        requestedBy instanceof HTMLElement
          ? requestedBy
          : document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      if (openTimer) window.clearTimeout(openTimer);
      openTimer = undefined;
      open(true);
    };
    const handleExit = (event: MouseEvent) => {
      if (armed && event.clientY <= 0 && !event.relatedTarget) openAutomaticallyWhenAvailable();
    };
    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable > 0 && window.scrollY / scrollable >= 0.5) openAutomaticallyWhenAvailable();
    };
    const handleSubmitted = (event: Event) => {
      if ((event as CustomEvent<NewsletterSource>).detail !== 'popup') setIsOpen(false);
    };

    if (forceMockPopup) {
      scheduleAutomaticOpen(300);
    } else if (canShowAutomatically) {
      scheduleAutomaticOpen(NEWSLETTER_POPUP_DELAY_MS);
      if (isTouch) {
        window.addEventListener('scroll', handleScroll, { passive: true });
      } else {
        armTimer = window.setTimeout(() => {
          armed = true;
        }, NEWSLETTER_POPUP_EXIT_INTENT_DELAY_MS);
        document.addEventListener('mouseout', handleExit);
      }
    }
    window.addEventListener(OPEN_EVENT, handleOpenRequested);
    window.addEventListener(SUBMITTED_EVENT, handleSubmitted);

    return () => {
      if (openTimer) window.clearTimeout(openTimer);
      if (armTimer) window.clearTimeout(armTimer);
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mouseout', handleExit);
      window.removeEventListener(OPEN_EVENT, handleOpenRequested);
      window.removeEventListener(SUBMITTED_EVENT, handleSubmitted);
    };
  }, [enabled, loading]);

  const close = () => {
    storePreference('dismissed', THIRTY_DAYS);
    setIsOpen(false);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      finalFocusRef={openerRef}
      returnFocusOnClose={Boolean(openerRef.current)}
      isCentered
      size={['full', '3xl']}
      motionPreset="slideInBottom"
      scrollBehavior="inside"
    >
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
      <ModalContent
        aria-label={newsletter_title}
        bg="gray.900"
        color="white"
        mx={[0, 4]}
        borderRadius={['none', 'xl']}
        overflow="hidden"
      >
        <ModalCloseButton
          zIndex={1}
          aria-label={closeLabel}
          boxSize="44px"
          top={3}
          right={3}
          color="white"
          bg="blackAlpha.600"
          border="1px solid"
          borderColor="whiteAlpha.500"
          _hover={{ bg: 'blackAlpha.800' }}
          _focusVisible={{ boxShadow: '0 0 0 3px white' }}
        />
        <ModalBody p={0}>
          <Box
            position="relative"
            h={['132px', '180px']}
            aria-hidden
          >
            <StaticAssetImage
              src="/acm.jpg"
              alt=""
              position="absolute"
              inset={0}
              objectFit="cover"
              objectPosition="center 58%"
              aria-hidden
            />
            <Box position="absolute" inset={0} bgGradient="linear(to-t, gray.900, transparent 72%)" />
            <Box position="absolute" insetX={0} bottom={0} h="4px" bgGradient="linear(to-r, brand, red.300)" />
          </Box>
          <NewsletterSignup source="popup" variant="modal" />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
